import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Grid,
  Button,
  Snackbar,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { CPEFlagsProps, IFlag } from './types';
import FlagCard from './FlagCard';
import { resolveCpeClaimFlag } from '@context/inspections';

const CPEFlags: React.FC<CPEFlagsProps> = ({ flags }) => {
  // Initialize with props.flags and keep in sync with useEffect
  const [localFlags, setLocalFlags] = useState<IFlag[]>(flags || []);
  const [selectedFlagIds, setSelectedFlagIds] = useState<string[]>([]);
  const [isResolving, setIsResolving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Important: Keep localFlags in sync with props.flags
  useEffect(() => {
    if (flags) {
      setLocalFlags(flags);
    }
  }, [flags]);

  if (!localFlags || localFlags.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          <AlertTitle>Sin Flags</AlertTitle>
          No se encontraron flags
        </Alert>
      </Box>
    );
  }

  const activeFlags = localFlags.filter((flag) => flag.status !== 'resolved');
  const resolvedFlags = localFlags.filter((flag) => flag.status === 'resolved');

  const sortedActiveFlags = [...activeFlags].sort((a, b) => {
    if (a.category === 'BLOCKING' && b.category !== 'BLOCKING') {
      return -1;
    }

    if (a.category !== 'BLOCKING' && b.category === 'BLOCKING') {
      return 1;
    }

    return 0;
  });

  const handleToggleFlag = (flagId: string) => {
    setSelectedFlagIds((prev) =>
      prev.includes(flagId)
        ? prev.filter((id) => id !== flagId)
        : [...prev, flagId],
    );
  };

  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (event.target.checked) {
      setSelectedFlagIds(activeFlags.map((flag) => flag.id));
    } else {
      setSelectedFlagIds([]);
    }
  };

  const handleResolveSelected = async () => {
    if (selectedFlagIds.length === 0) {
      return;
    }

    setIsResolving(true);

    try {
      const selectedFlags = localFlags.filter((flag) =>
        selectedFlagIds.includes(flag.id),
      );

      const resolveResults = await Promise.all(
        selectedFlags.map(async (flag) => {
          try {
            await resolveCpeClaimFlag(flag.claimId, flag.id);

            return { id: flag.id, success: true };
          } catch (err) {
            console.error(err);
            return { id: flag.id, success: false };
          }
        }),
      );

      const resolvedFlagIds = resolveResults
        .filter((result) => result.success)
        .map((result) => result.id);

      const updatedFlags = localFlags.map((flag) =>
        resolvedFlagIds.includes(flag.id)
          ? { ...flag, status: 'resolved' }
          : flag,
      );

      setLocalFlags(updatedFlags);

      // Always show a success message if at least some flags were resolved
      if (resolvedFlagIds.length > 0) {
        setSnackbarMessage(
          resolvedFlagIds.length === selectedFlagIds.length
            ? `${resolvedFlagIds.length} flag${
                resolvedFlagIds.length > 1 ? 's' : ''
              } resuelto${resolvedFlagIds.length > 1 ? 's' : ''} exitosamente`
            : `${resolvedFlagIds.length} de ${selectedFlagIds.length} flags resueltos exitosamente`,
        );
      } else {
        // Only show error if none were resolved
        setSnackbarMessage(
          'No se resolvieron flags. Por favor, intente nuevamente.',
        );
      }

      setSnackbarOpen(true);

      setSelectedFlagIds([]);
    } catch (error) {
      // This should only happen if there's a critical error outside the resolution process
      console.error(error);
      setSnackbarMessage(`Error: ${error}`);
      setSnackbarOpen(true);
    } finally {
      setIsResolving(false);
    }
  };

  const allSelected =
    activeFlags.length > 0 && selectedFlagIds.length === activeFlags.length;
  const someSelected = selectedFlagIds.length > 0 && !allSelected;

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      {activeFlags.length > 0 ? (
        <Box sx={{ mb: 4, width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              width: '100%',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mr: 2 }}>
                Flags Activos ({activeFlags.length})
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAllChange}
                    disabled={activeFlags.length === 0 || isResolving}
                  />
                }
                label="Seleccionar Todos"
              />
            </Box>
            <Button
              variant="contained"
              color="success"
              disabled={isResolving || selectedFlagIds.length === 0}
              onClick={handleResolveSelected}
            >
              {isResolving
                ? `Resolviendo ${selectedFlagIds.length} flag${
                    selectedFlagIds.length > 1 ? 's' : ''
                  }...`
                : `Resolver Seleccionados (${selectedFlagIds.length})`}
            </Button>
          </Box>
          <Grid container spacing={2}>
            {sortedActiveFlags.map((flag) => (
              <Grid item xs={12} sm={6} md={4} key={flag.id}>
                <FlagCard
                  flag={flag}
                  isSelected={selectedFlagIds.includes(flag.id)}
                  onToggleSelect={() => handleToggleFlag(flag.id)}
                  isLoading={isResolving}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Flags Activos (0)
          </Typography>
          <Alert severity="info">
            <AlertTitle>Sin Flags Activos</AlertTitle>
            No hay flags activos para mostrar.
          </Alert>
        </Box>
      )}

      {resolvedFlags.length > 0 ? (
        <Box sx={{ width: '100%' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Flags Resueltos ({resolvedFlags.length})
          </Typography>
          <Grid container spacing={2}>
            {resolvedFlags.map((flag) => (
              <Grid item xs={12} sm={6} md={4} key={flag.id}>
                <FlagCard
                  flag={flag}
                  isSelected={false}
                  onToggleSelect={() => {
                    /* Empty function to satisfy prop type */
                  }}
                  isLoading={false}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Flags Resueltos (0)
          </Typography>
          <Alert severity="info">
            <AlertTitle>Sin Flags Resueltos</AlertTitle>
            No hay flags resueltos para mostrar.
          </Alert>
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '24px', // Position from top
          '& .MuiSnackbarContent-root': {
            width: '100%',
            minWidth: '300px',
            justifyContent: 'center',
            fontWeight: 500,
          },
        }}
      />
    </Box>
  );
};

export default CPEFlags;
